import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppScreen } from '../../components/AppScreen';
import {
  CheckoutBreakModal,
  type CheckoutBreakValues,
} from '../../components/CheckoutBreakModal';
import { KioskActivationPanel } from '../../components/KioskActivationPanel';
import { KioskFaceCapture } from '../../components/KioskFaceCapture';
import { KioskVisitorFlow } from '../../components/KioskVisitorFlow';
import { getKioskDeviceConfig, getKioskIdleResetMs } from '../../config/kioskConfig';
import { darkColors, lightColors } from '../../constants/colors';
import { routes, type KioskStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getAttendanceDetails,
  recordBreakTimesForCheckout,
} from '../../services/attendanceService';
import {
  runKioskFacePunch,
  submitVerifiedAttendanceAction,
} from '../../services/faceAttendancePunchService';
import {
  ensureKioskSession,
  isKioskActivated,
  registerKioskSessionInvalidationHandler,
  restoreKioskSession,
} from '../../services/kioskAuthService';
import type {
  AttendanceCapture,
  FacePunchResult,
  SelectableAttendanceAction,
} from '../../types/faceAttendance';
import {
  currentBusinessClock,
  breakStateFromEvents,
  resolveCheckoutBreakPrompt,
  timestampToClock,
} from '../../utils/checkoutBreak';
import {
  formatMinutes,
  formatTime,
  getGreeting,
  todayBusinessCalendarDate,
} from '../../utils/dateTime';
import { kioskFriendlyMessage, toKioskFaceError } from '../../utils/kioskErrors';
import { releaseCapturedImage } from '../../utils/temporaryImage';
import { isWebPlatform } from '../../utils/webLayout';

function actionFailure(error: unknown): { code: string; message: string } {
  const flowError = toKioskFaceError(error);
  return { code: flowError.code, message: flowError.message };
}

type Phase =
  | 'idle'
  | 'checking'
  | 'activating'
  | 'capturing'
  | 'submitting'
  | 'actionChoice'
  | 'success'
  | 'visitor'
  | 'error';

type SuccessState = {
  displayName: string;
  employeeNumber: string | null;
  action: FacePunchResult['action'];
  eventTime: string;
  workedMinutes: number | null;
  breakMinutes: number | null;
};

type PendingActionState = {
  employeeId: number;
  displayName: string;
  employeeNumber: string | null;
  faceVerificationLogId: number;
  attendanceMode: string;
  livenessSessionId: number | null;
  isOnBreak: boolean;
};

function isOnBreakFromActions(
  actions: FacePunchResult['availableActions'] | undefined,
): boolean {
  return actions?.includes('BREAK_IN') === true;
}

function checkoutBreakPromptForKiosk(
  details: Awaited<ReturnType<typeof getAttendanceDetails>>,
  fallbackOnBreak: boolean,
  fallbackStartedAt: string | null,
) {
  if (!details) {
    return {
      prompt: fallbackOnBreak
        ? ({ kind: 'open', startedAt: fallbackStartedAt } as const)
        : ({ kind: 'none' } as const),
      checkInClock: null as string | null,
      openBreakStartedAt: fallbackStartedAt,
    };
  }
  const breakState = breakStateFromEvents(details.events);
  const isOnBreak = fallbackOnBreak || breakState.openBreakStartedAt != null;
  return {
    prompt: resolveCheckoutBreakPrompt({
      expectedWorkMinutes: details.durations.expectedWorkMinutes,
      currentStatus: details.statusCode,
      breakMinutes: details.durations.breakMinutes,
      isOnBreak,
      canEndBreak: isOnBreak,
      openBreakStartedAt: breakState.openBreakStartedAt ?? fallbackStartedAt,
      hasCompletedBreak: breakState.hasCompletedBreak,
    }),
    checkInClock: timestampToClock(
      details.checkIn.localTimestamp ?? details.checkIn.timestamp,
    ),
    openBreakStartedAt: breakState.openBreakStartedAt ?? fallbackStartedAt,
  };
}

async function resolveCompletedBreakMinutes(
  employeeId: number,
  hinted: number | null,
): Promise<number | null> {
  if (hinted != null && hinted > 0) return hinted;
  try {
    const details = await getAttendanceDetails(todayBusinessCalendarDate(), {
      employeeId,
    });
    if (!details) return hinted;
    const fromEvents = breakStateFromEvents(details.events).completedBreakMinutes;
    const stored = details.durations.breakMinutes;
    if (stored != null && stored > 0) return stored;
    return fromEvents ?? stored ?? hinted;
  } catch {
    return hinted;
  }
}

/** A face was found but matched no employee: offer the visitor options instead. */
const UNMATCHED_FACE_CODE = 'FACE_NOT_FOUND';

const FACE_NOT_REGISTERED_CODES = new Set([
  'FACE_NOT_REGISTERED',
  'FACE_REGISTRATION_NOT_ACTIVE',
  'FACE_REGISTRATION_NOT_FOUND',
  'FACE_EMPLOYEE_MAPPING_NOT_FOUND',
  'FACE_ATTENDANCE_NOT_ENABLED',
]);

const VERIFICATION_FAILED_CODES = new Set([
  'FACE_NOT_FOUND',
  'FACE_NOT_DETECTED',
  'FACE_IDENTITY_MISMATCH',
  'LIVENESS_FAILED',
  'LIVENESS_CANCELED',
  'LIVENESS_NOT_COMPLETED',
  'LIVENESS_SESSION_EXPIRED',
  'LIVENESS_RESULT_TIMEOUT',
  'CAPTURE_FAILED',
  'CAPTURE_CANCELED',
]);

function titleCaseGreeting(date = new Date()): string {
  return getGreeting(date).replace(/\b\w/g, char => char.toUpperCase());
}

export function KioskAttendanceScreen() {
  const palette = useAppColors();
  const isDark = palette.background === darkColors.background;
  const navigation =
    useNavigation<NativeStackNavigationProp<KioskStackParamList>>();
  const [phase, setPhase] = useState<Phase>('idle');
  const [success, setSuccess] = useState<SuccessState | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingActionState | null>(null);
  const [breakPrompt, setBreakPrompt] = useState<'missing' | 'open' | null>(null);
  const [breakCheckInClock, setBreakCheckInClock] = useState<string | null>(null);
  const [openBreakStartedAt, setOpenBreakStartedAt] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [returnSecondsLeft, setReturnSecondsLeft] = useState<number | null>(
    null,
  );
  // The unmatched capture, kept for the visitor flow until it closes.
  const [visitorFace, setVisitorFace] = useState<AttendanceCapture | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleResetMs = useRef(4000);
  const punchInFlightRef = useRef(false);

  const openLogin = useCallback(() => {
    navigation.navigate(routes.login);
  }, [navigation]);

  const themed = useMemo(
    () => ({
      brand: {
        ...typography.caption,
        color: palette.white,
        opacity: 0.85,
        letterSpacing: 1.2,
        textTransform: 'uppercase' as const,
        fontWeight: '700' as const,
      },
      title: {
        fontSize: 40,
        lineHeight: 48,
        fontWeight: '700' as const,
        color: palette.white,
        textAlign: 'center' as const,
      },
      subtitle: {
        fontSize: 20,
        lineHeight: 28,
        color: palette.white,
        opacity: 0.9,
        textAlign: 'center' as const,
      },
      successHeading: {
        fontSize: 22,
        lineHeight: 30,
        fontWeight: '600' as const,
        color: palette.success,
        textAlign: 'center' as const,
      },
      resultName: {
        fontSize: 22,
        lineHeight: 30,
        fontWeight: '700' as const,
        color: palette.info,
        textAlign: 'center' as const,
      },
      employeeId: {
        fontSize: 16,
        lineHeight: 24,
        fontWeight: '500' as const,
        color: palette.textMuted,
        textAlign: 'center' as const,
      },
      metricLabel: {
        ...typography.caption,
        color: palette.textMuted,
        textAlign: 'center' as const,
      },
      metricValue: {
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '700' as const,
        color: palette.textPrimary,
        textAlign: 'center' as const,
      },
      greeting: {
        fontSize: 20,
        lineHeight: 28,
        fontWeight: '700' as const,
        color: palette.textPrimary,
        textAlign: 'center' as const,
      },
      countdown: {
        fontSize: 14,
        lineHeight: 20,
        color: palette.textMuted,
        textAlign: 'center' as const,
      },
      resultTitle: {
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '700' as const,
        color: palette.textPrimary,
        textAlign: 'center' as const,
      },
      resultBody: {
        fontSize: 16,
        lineHeight: 24,
        color: palette.textPrimary,
        textAlign: 'center' as const,
      },
      resultBodyEmphasis: {
        fontSize: 16,
        lineHeight: 24,
        fontWeight: '600' as const,
        color: palette.textPrimary,
        textAlign: 'center' as const,
      },
    }),
    [palette],
  );

  useEffect(() => {
    try {
      idleResetMs.current = getKioskIdleResetMs();
    } catch {
      idleResetMs.current = 4000;
    }
    restoreKioskSession().catch(() => undefined);
    registerKioskSessionInvalidationHandler(message => {
      setStatusMessage(message);
    });
    return () => registerKioskSessionInvalidationHandler(null);
  }, []);

  const clearResetTimer = useCallback(() => {
    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
      resetTimer.current = null;
    }
  }, []);

  useEffect(() => {
    if (!visitorFace) return;
    return () => {
      releaseCapturedImage(visitorFace.uri).catch(() => undefined);
    };
  }, [visitorFace]);

  const returnToIdle = useCallback(() => {
    clearResetTimer();
    punchInFlightRef.current = false;
    setVisitorFace(null);
    setPhase('idle');
    setSuccess(null);
    setPendingAction(null);
    setBreakPrompt(null);
    setBreakCheckInClock(null);
    setOpenBreakStartedAt(null);
    setErrorMessage(null);
    setErrorCode(null);
    setReturnSecondsLeft(null);
  }, [clearResetTimer]);

  const scheduleIdleReturn = useCallback(() => {
    clearResetTimer();
    resetTimer.current = setTimeout(() => {
      punchInFlightRef.current = false;
      setPhase('idle');
      setSuccess(null);
      setPendingAction(null);
      setBreakPrompt(null);
      setBreakCheckInClock(null);
      setOpenBreakStartedAt(null);
      setErrorMessage(null);
      setErrorCode(null);
      setReturnSecondsLeft(null);
      resetTimer.current = null;
    }, idleResetMs.current);
  }, [clearResetTimer]);

  useEffect(() => () => clearResetTimer(), [clearResetTimer]);

  useEffect(() => {
    if (phase !== 'success') return;
    const tick = setInterval(() => {
      setReturnSecondsLeft(prev =>
        prev === null || prev <= 1 ? prev : prev - 1,
      );
    }, 1000);
    return () => clearInterval(tick);
  }, [phase]);

  const beginFaceCapture = useCallback(() => {
    punchInFlightRef.current = false;
    setSuccess(null);
    setPendingAction(null);
    setBreakPrompt(null);
    setBreakCheckInClock(null);
    setOpenBreakStartedAt(null);
    setErrorMessage(null);
    setErrorCode(null);
    setStatusMessage(null);
    setReturnSecondsLeft(null);
    setPhase('capturing');
  }, []);

  const showBlockedStatus = useCallback(
    (message: string, code = 'KIOSK_INACTIVE') => {
      setErrorCode(code);
      setErrorMessage(message);
      setPhase('error');
      scheduleIdleReturn();
    },
    [scheduleIdleReturn],
  );

  const startMarkAttendance = useCallback(async () => {
    if (phase !== 'idle') return;
    clearResetTimer();
    punchInFlightRef.current = false;
    setSuccess(null);
    setErrorMessage(null);
    setErrorCode(null);
    setPhase('checking');

    try {
      await ensureKioskSession();
      if (isKioskActivated()) {
        beginFaceCapture();
        return;
      }
      setPhase('activating');
    } catch (error) {
      const code =
        typeof error === 'object' &&
        error &&
        'code' in error &&
        typeof (error as { code?: unknown }).code === 'string'
          ? (error as { code: string }).code
          : '';
      const message = error instanceof Error ? error.message : '';

      if (
        code === 'KIOSK_INACTIVE' ||
        code === 'KIOSK_UNAUTHORIZED' ||
        code === 'KIOSK_DEVICE_MISMATCH'
      ) {
        showBlockedStatus(kioskFriendlyMessage(code, message), code);
        return;
      }

      // Not registered yet — open activation (scan QR / paste token).
      setPhase('activating');
    }
  }, [beginFaceCapture, clearResetTimer, phase, showBlockedStatus]);

  const submitCapture = useCallback(
    async (image: AttendanceCapture) => {
      if (punchInFlightRef.current) return;
      punchInFlightRef.current = true;
      setPhase('submitting');
      let keepForVisitor = false;
      try {
        const result = await runKioskFacePunch(image, { retainImage: true });
        if (result.action === 'ACTION_REQUIRED') {
          const fallbackOnBreak = isOnBreakFromActions(result.availableActions);
          let isOnBreak = fallbackOnBreak;
          try {
            const details = await getAttendanceDetails(
              todayBusinessCalendarDate(),
              { employeeId: result.employee.employeeId },
            );
            const resolved = checkoutBreakPromptForKiosk(
              details,
              fallbackOnBreak,
              null,
            );
            isOnBreak =
              fallbackOnBreak || resolved.openBreakStartedAt != null;
            setBreakCheckInClock(resolved.checkInClock);
            setOpenBreakStartedAt(resolved.openBreakStartedAt);
          } catch {
            // If today's details are unavailable, fall back to punch actions.
          }
          setPendingAction({
            employeeId: result.employee.employeeId,
            displayName: result.employee.displayName || 'Employee',
            employeeNumber: result.employee.employeeNumber,
            faceVerificationLogId: result.faceVerificationLogId,
            // Face-only punch persists WEB_PORTAL. OFFICE_KIOSK requires a
            // livenessSessionId on the follow-up check-out / break call.
            attendanceMode:
              result.livenessSessionId
                ? result.attendanceMode || 'OFFICE_KIOSK'
                : result.attendanceMode && result.attendanceMode !== 'OFFICE_KIOSK'
                  ? result.attendanceMode
                  : 'WEB_PORTAL',
            livenessSessionId: result.livenessSessionId,
            isOnBreak,
          });
          punchInFlightRef.current = false;
          setPhase('actionChoice');
          return;
        }
        const breakMinutes =
          result.action === 'BREAK_IN' || result.action === 'CHECK_OUT'
            ? await resolveCompletedBreakMinutes(
                result.employee.employeeId,
                result.breakMinutes ?? null,
              )
            : (result.breakMinutes ?? null);
        setSuccess({
          displayName: result.employee.displayName || 'Employee',
          employeeNumber: result.employee.employeeNumber,
          action: result.action,
          eventTime: result.eventTime,
          workedMinutes: result.workedMinutes,
          breakMinutes,
        });
        setReturnSecondsLeft(Math.max(1, Math.ceil(idleResetMs.current / 1000)));
        setPhase('success');
        scheduleIdleReturn();
      } catch (error) {
        punchInFlightRef.current = false;
        const failure = actionFailure(error);
        if (failure.code === UNMATCHED_FACE_CODE) {
          // The visitor flow owns its own timers; no idle reset while they choose.
          // It reuses this capture to recognise or register the visitor.
          keepForVisitor = true;
          setVisitorFace(image);
          setPhase('visitor');
          return;
        }
        setErrorCode(failure.code);
        setErrorMessage(failure.message);
        setPhase('error');
        scheduleIdleReturn();
      } finally {
        if (!keepForVisitor) releaseCapturedImage(image?.uri).catch(() => undefined);
      }
    },
    [scheduleIdleReturn],
  );

  const finishVerifiedCheckout = useCallback(
    async (
      pending: PendingActionState,
      breakTimes?: { breakStartTime: string; breakEndTime: string },
    ) => {
      const kiosk = getKioskDeviceConfig();
      const result = await submitVerifiedAttendanceAction({
        action: 'CHECK_OUT',
        employeeId: pending.employeeId,
        faceVerificationLogId: pending.faceVerificationLogId,
        attendanceMode: pending.attendanceMode,
        kioskId: kiosk.kioskId,
        deviceIdentifier: kiosk.deviceIdentifier,
        livenessSessionId: pending.livenessSessionId,
        breakStartTime: breakTimes?.breakStartTime,
        breakEndTime: breakTimes?.breakEndTime,
      });
      const breakMinutes = await resolveCompletedBreakMinutes(
        pending.employeeId,
        result.breakMinutes ?? null,
      );
      setSuccess({
        displayName: pending.displayName,
        employeeNumber: pending.employeeNumber,
        action: result.action,
        eventTime: result.eventTime,
        workedMinutes: result.workedMinutes,
        breakMinutes,
      });
      setPendingAction(null);
      setBreakPrompt(null);
      setReturnSecondsLeft(Math.max(1, Math.ceil(idleResetMs.current / 1000)));
      setPhase('success');
      scheduleIdleReturn();
    },
    [scheduleIdleReturn],
  );

  const chooseVerifiedAction = useCallback(
    async (action: SelectableAttendanceAction) => {
      if (!pendingAction || punchInFlightRef.current) return;
      punchInFlightRef.current = true;
      setPhase('submitting');
      try {
        if (action === 'CHECK_OUT') {
          let details: Awaited<ReturnType<typeof getAttendanceDetails>> =
            null;
          try {
            details = await getAttendanceDetails(
              todayBusinessCalendarDate(),
              { employeeId: pendingAction.employeeId },
            );
          } catch {
            // Use the face-verified on-break hint when today's details fail.
          }
          const resolved = checkoutBreakPromptForKiosk(
            details,
            pendingAction.isOnBreak,
            openBreakStartedAt,
          );
          if (resolved.checkInClock) {
            setBreakCheckInClock(resolved.checkInClock);
          }
          if (resolved.openBreakStartedAt) {
            setOpenBreakStartedAt(resolved.openBreakStartedAt);
          }
          if (resolved.prompt.kind !== 'none') {
            punchInFlightRef.current = false;
            setBreakPrompt(resolved.prompt.kind);
            setPhase('actionChoice');
            return;
          }
          await finishVerifiedCheckout(pendingAction);
          return;
        }

        const kiosk = getKioskDeviceConfig();
        const result = await submitVerifiedAttendanceAction({
          action,
          employeeId: pendingAction.employeeId,
          faceVerificationLogId: pendingAction.faceVerificationLogId,
          attendanceMode: pendingAction.attendanceMode,
          kioskId: kiosk.kioskId,
          deviceIdentifier: kiosk.deviceIdentifier,
          livenessSessionId: pendingAction.livenessSessionId,
        });
        const breakMinutes =
          result.action === 'BREAK_IN'
            ? await resolveCompletedBreakMinutes(
                pendingAction.employeeId,
                result.breakMinutes ?? null,
              )
            : (result.breakMinutes ?? null);
        setSuccess({
          displayName: pendingAction.displayName,
          employeeNumber: pendingAction.employeeNumber,
          action: result.action,
          eventTime: result.eventTime,
          workedMinutes: result.workedMinutes,
          breakMinutes,
        });
        setPendingAction(null);
        setReturnSecondsLeft(Math.max(1, Math.ceil(idleResetMs.current / 1000)));
        setPhase('success');
        scheduleIdleReturn();
      } catch (error) {
        punchInFlightRef.current = false;
        const failure = actionFailure(error);
        setErrorCode(failure.code);
        setErrorMessage(failure.message);
        setPhase('error');
      }
    },
    [finishVerifiedCheckout, openBreakStartedAt, pendingAction, scheduleIdleReturn],
  );

  const submitKioskBreakAndCheckout = useCallback(
    async (values: CheckoutBreakValues) => {
      if (!pendingAction || punchInFlightRef.current) return;
      punchInFlightRef.current = true;
      setPhase('submitting');
      try {
        if (values.recordBreak && !values.recordBreakStart) {
          const kiosk = getKioskDeviceConfig();
          await recordBreakTimesForCheckout({
            attendanceDate: todayBusinessCalendarDate(),
            startClock: values.startClock,
            endClock: values.endClock,
            recordBreakStart: false,
            employeeId: pendingAction.employeeId,
            attendanceSource: pendingAction.attendanceMode,
            faceVerificationLogId: pendingAction.faceVerificationLogId,
            kioskId: kiosk.kioskId,
            deviceIdentifier: kiosk.deviceIdentifier,
            livenessSessionId: pendingAction.livenessSessionId,
          });
          await finishVerifiedCheckout(pendingAction);
        } else {
          await finishVerifiedCheckout(
            pendingAction,
            values.recordBreak
              ? {
                  breakStartTime: values.startClock,
                  breakEndTime: values.endClock,
                }
              : undefined,
          );
        }
      } catch (error) {
        punchInFlightRef.current = false;
        const failure = actionFailure(error);
        setErrorCode(failure.code);
        setErrorMessage(failure.message);
        setPhase('error');
      }
    },
    [finishVerifiedCheckout, pendingAction],
  );

  const onCaptureError = useCallback(
    (message: string) => {
      setErrorCode('CAPTURE_FAILED');
      setErrorMessage(message);
      setPhase('error');
      scheduleIdleReturn();
    },
    [scheduleIdleReturn],
  );

  if (phase === 'activating') {
    return (
      <KioskActivationPanel
        message={statusMessage}
        preferScan
        onCancel={returnToIdle}
        onActivated={() => {
          setStatusMessage(null);
          beginFaceCapture();
        }}
      />
    );
  }

  if (phase === 'checking') {
    return (
      <AppScreen contentStyle={styles.center} backgroundColor={palette.brand}>
        <ActivityIndicator size="large" color={palette.white} />
        <Text style={[themed.subtitle, { marginTop: spacing.lg }]}>
          Checking kiosk…
        </Text>
      </AppScreen>
    );
  }

  if (phase === 'capturing') {
    return (
      <View style={styles.cameraHost}>
        <KioskFaceCapture
          onCaptured={submitCapture}
          onError={onCaptureError}
          onTimeout={returnToIdle}
        />
      </View>
    );
  }

  if (phase === 'submitting') {
    return (
      <AppScreen contentStyle={styles.center} backgroundColor={palette.brand}>
        <ActivityIndicator size="large" color={palette.white} />
        <Text
          accessibilityRole="progressbar"
          accessibilityLabel="Marking attendance"
          style={[themed.subtitle, { marginTop: spacing.lg }]}
        >
          Marking attendance…
        </Text>
      </AppScreen>
    );
  }

  if (phase === 'visitor') {
    return (
      <KioskVisitorFlow
        onExit={returnToIdle}
        welcomeDisplayMs={idleResetMs.current}
        faceImage={visitorFace}
      />
    );
  }

  if (phase === 'actionChoice' && pendingAction) {
    return (
      <AppScreen contentStyle={styles.center}>
        <View
          style={[
            styles.resultCard,
            { backgroundColor: palette.surface, borderColor: palette.border },
          ]}
        >
          <View style={[styles.successIcon, { backgroundColor: palette.success }]}>
            <Text style={[styles.successIconMark, { color: palette.onSuccess }]}>✓</Text>
          </View>
          <Text style={themed.successHeading}>Face Verified</Text>
          <View style={styles.identityBlock}>
            <Text style={themed.resultName}>{pendingAction.displayName}</Text>
            {pendingAction.employeeNumber ? (
              <Text style={themed.employeeId}>{pendingAction.employeeNumber}</Text>
            ) : null}
          </View>
          <Text style={themed.resultBody}>What would you like to do?</Text>
          <View style={styles.actionChoiceRow}>
            {pendingAction.isOnBreak ? (
              <AppButton
                title="End Break"
                onPress={() => chooseVerifiedAction('BREAK_IN')}
                style={styles.actionChoiceButton}
              />
            ) : (
              <AppButton
                title="Start Break"
                onPress={() => chooseVerifiedAction('BREAK_OUT')}
                style={styles.actionChoiceButton}
              />
            )}
            <AppButton
              title="Check Out"
              variant="secondary"
              onPress={() => chooseVerifiedAction('CHECK_OUT')}
              style={styles.actionChoiceButton}
            />
          </View>
          <AppButton title="Cancel" variant="text" onPress={returnToIdle} />
        </View>
        <CheckoutBreakModal
          visible={breakPrompt !== null}
          mode={breakPrompt ?? 'missing'}
          checkInClock={breakCheckInClock}
          checkOutClock={currentBusinessClock()}
          initialStart={openBreakStartedAt}
          onCancel={() => setBreakPrompt(null)}
          onConfirm={submitKioskBreakAndCheckout}
        />
      </AppScreen>
    );
  }

  if (phase === 'success' && success) {
    const presentation = {
      CHECK_IN: ['Check In Successful', 'Check In Time'],
      CHECK_OUT: ['Check Out Successful', 'Check Out Time'],
      BREAK_OUT: ['Break Started', 'Break Start Time'],
      BREAK_IN: ['Break Ended', 'Break End Time'],
      ACTION_REQUIRED: ['Face Verified', 'Verification Time'],
    } as const;
    const [heading, timeLabel] = presentation[success.action];
    const showBreakHours =
      success.action === 'BREAK_IN' || success.action === 'CHECK_OUT';
    const showWorkedHours = success.action !== 'BREAK_IN';
    const workedLabel =
      success.workedMinutes != null
        ? formatMinutes(success.workedMinutes)
        : '—';
    const breakLabel =
      success.breakMinutes != null
        ? formatMinutes(success.breakMinutes)
        : '—';
    const seconds =
      returnSecondsLeft ?? Math.max(1, Math.ceil(idleResetMs.current / 1000));

    return (
      <AppScreen contentStyle={styles.center}>
        <View
          style={[
            styles.resultCard,
            {
              backgroundColor: palette.surface,
              borderColor: palette.border,
            },
          ]}
          accessibilityRole="summary"
          accessibilityLabel={`${heading} for ${success.displayName}`}
        >
          <View
            style={[styles.successIcon, { backgroundColor: palette.success }]}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            <Text style={[styles.successIconMark, { color: palette.onSuccess }]}>
              ✓
            </Text>
          </View>

          <Text style={themed.successHeading}>{heading}</Text>

          <View style={styles.identityBlock}>
            <Text style={themed.resultName}>{success.displayName}</Text>
            {success.employeeNumber?.trim() ? (
              <Text style={themed.employeeId}>
                {success.employeeNumber.trim()}
              </Text>
            ) : null}
          </View>

          <View style={styles.metricsRow}>
            <View style={styles.metricCell}>
              <Text style={themed.metricLabel}>{timeLabel}</Text>
              <Text style={themed.metricValue}>
                {formatTime(success.eventTime)}
              </Text>
            </View>
            {showWorkedHours ? (
              <View style={styles.metricCell}>
                <Text style={themed.metricLabel}>Worked Hours</Text>
                <Text style={themed.metricValue}>{workedLabel}</Text>
              </View>
            ) : null}
            {showBreakHours ? (
              <View style={styles.metricCell}>
                <Text style={themed.metricLabel}>Total Break Hours</Text>
                <Text style={themed.metricValue}>{breakLabel}</Text>
              </View>
            ) : null}
          </View>

          <View
            style={[styles.successDivider, { backgroundColor: palette.border }]}
          />

          <View style={styles.successFooter}>
            <Text style={themed.greeting}>{titleCaseGreeting()}!</Text>
            <Text style={themed.countdown}>
              Returning to home in {seconds} sec...
            </Text>
          </View>
        </View>
      </AppScreen>
    );
  }

  if (phase === 'error' && errorMessage) {
    const notRegistered = FACE_NOT_REGISTERED_CODES.has(errorCode ?? '');
    const verificationFailed = VERIFICATION_FAILED_CODES.has(errorCode ?? '');

    if (notRegistered) {
      return (
        <AppScreen contentStyle={styles.center}>
          <View
            style={[
              styles.resultCard,
              {
                backgroundColor: palette.surface,
                borderColor: palette.border,
              },
            ]}
            accessibilityRole="alert"
            accessibilityLabel="Face not registered"
          >
            <View
              style={[
                styles.notRegisteredIcon,
                {
                  backgroundColor: isDark ? '#3A2A20' : '#F3E0D0',
                },
              ]}
            >
              <View style={styles.personSilhouette}>
                <View
                  style={[
                    styles.personHead,
                    { backgroundColor: isDark ? '#D4A574' : '#C47A3A' },
                  ]}
                />
                <View
                  style={[
                    styles.personShoulders,
                    { backgroundColor: isDark ? '#D4A574' : '#C47A3A' },
                  ]}
                />
              </View>
            </View>
            <Text style={themed.resultTitle}>Face Not Registered</Text>
            <View style={styles.resultCopyBlock}>
              <Text style={themed.resultBody}>{errorMessage}</Text>
            </View>
            <AppButton
              title="OK"
              onPress={returnToIdle}
              style={StyleSheet.flatten([
                styles.resultCta,
                {
                  backgroundColor: palette.info,
                  borderColor: palette.info,
                },
              ])}
              accessibilityLabel="OK, Return To Idle"
            />
          </View>
        </AppScreen>
      );
    }

    if (verificationFailed) {
      return (
        <AppScreen contentStyle={styles.center}>
          <View
            style={[
              styles.resultCard,
              {
                backgroundColor: palette.surface,
                borderColor: palette.border,
              },
            ]}
            accessibilityRole="alert"
            accessibilityLabel="Verification failed"
          >
            <View
              style={[styles.failureIcon, { backgroundColor: palette.error }]}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            >
              <Text style={[styles.failureIconMark, { color: palette.onError }]}>
                ✕
              </Text>
            </View>
            <Text style={themed.resultTitle}>Verification Failed</Text>
            <View style={styles.resultCopyBlock}>
              <Text style={themed.resultBody}>{errorMessage}</Text>
            </View>
            <AppButton
              title="Try Again"
              onPress={returnToIdle}
              style={StyleSheet.flatten([
                styles.resultCta,
                {
                  backgroundColor: palette.info,
                  borderColor: palette.info,
                },
              ])}
              accessibilityLabel="Try Again"
            />
          </View>
        </AppScreen>
      );
    }

    return (
      <AppScreen contentStyle={styles.center}>
        <View
          style={[
            styles.resultCard,
            {
              backgroundColor: palette.surface,
              borderColor: palette.border,
            },
          ]}
          accessibilityRole="alert"
        >
          <View
            style={[styles.failureIcon, { backgroundColor: palette.error }]}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            <Text style={[styles.failureIconMark, { color: palette.onError }]}>
              ✕
            </Text>
          </View>
          <Text style={themed.resultTitle}>Unable to mark attendance</Text>
          <Text style={themed.resultBody}>{errorMessage}</Text>
          <AppButton
            title="Try Again"
            onPress={returnToIdle}
            style={StyleSheet.flatten([
              styles.resultCta,
              {
                backgroundColor: palette.info,
                borderColor: palette.info,
              },
            ])}
            accessibilityLabel="Return to idle and try again"
          />
        </View>
      </AppScreen>
    );
  }

  return (
    <AppScreen contentStyle={styles.idle} backgroundColor={palette.brand}>
      <View style={styles.idleTopBar}>
        <View style={styles.idleTopBarSpacer} />
        <Pressable
          onPress={openLogin}
          accessibilityRole="button"
          accessibilityLabel="Login"
          hitSlop={10}
          style={({ pressed }) => [
            styles.loginButton,
            {
              backgroundColor: pressed
                ? 'rgba(255,255,255,0.22)'
                : 'rgba(255,255,255,0.14)',
              borderColor: 'rgba(255,255,255,0.28)',
            },
          ]}
        >
          <View style={styles.loginIcon}>
            <View
              style={[styles.loginIconHead, { borderColor: palette.white }]}
            />
            <View
              style={[styles.loginIconBody, { borderColor: palette.white }]}
            />
          </View>
        </Pressable>
      </View>
      <View style={styles.idleCopy}>
        <Text style={themed.brand}>Office kiosk</Text>
        <Text style={themed.title}>Mark Attendance</Text>
        <Text style={themed.subtitle}>
          Stand in front of the camera and tap below to check in, manage a
          break, or check out.
        </Text>
      </View>
      <AppButton
        title="Mark Attendance"
        onPress={() => {
          startMarkAttendance().catch(() => undefined);
        }}
        variant="secondary"
        titleColor={lightColors.textPrimary}
        style={StyleSheet.flatten([
          styles.primaryCta,
          isWebPlatform() ? styles.primaryCtaWeb : null,
          {
            backgroundColor: palette.white,
            borderColor: palette.white,
          },
        ])}
        accessibilityLabel="Mark Attendance"
      />
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  center: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.lg,
  },
  cameraHost: { flex: 1 },
  idle: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingVertical: spacing.xxl,
  },
  idleTopBar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 48,
  },
  idleTopBarSpacer: {
    flex: 1,
  },
  loginButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginIcon: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  loginIconHead: {
    width: 8,
    height: 8,
    borderRadius: 4,
    borderWidth: 1.5,
    marginBottom: 2,
  },
  loginIconBody: {
    width: 14,
    height: 7,
    borderTopLeftRadius: 7,
    borderTopRightRadius: 7,
    borderWidth: 1.5,
    borderBottomWidth: 0,
  },
  idleCopy: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.md,
  },
  primaryCta: {
    minHeight: 72,
    borderRadius: 16,
  },
  /** Web: keep the kiosk CTA the same width as the result card on large screens. */
  primaryCtaWeb: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
  },
  resultCard: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 20,
    borderWidth: 1,
    paddingVertical: spacing.xxl,
    paddingHorizontal: spacing.xl,
    gap: spacing.lg,
    alignItems: 'center',
  },
  successIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successIconMark: {
    fontSize: 40,
    lineHeight: 44,
    fontWeight: '700',
  },
  failureIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  failureIconMark: {
    fontSize: 36,
    lineHeight: 40,
    fontWeight: '700',
  },
  notRegisteredIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  personSilhouette: {
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: 40,
  },
  personHead: {
    width: 18,
    height: 18,
    borderRadius: 9,
    marginBottom: 4,
  },
  personShoulders: {
    width: 34,
    height: 16,
    borderTopLeftRadius: 17,
    borderTopRightRadius: 17,
  },
  identityBlock: {
    alignItems: 'center',
    gap: spacing.xs,
    width: '100%',
  },
  metricsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    gap: spacing.md,
    justifyContent: 'center',
  },
  metricCell: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 96,
    alignItems: 'center',
    gap: spacing.xs,
  },
  successDivider: {
    alignSelf: 'stretch',
    height: StyleSheet.hairlineWidth,
  },
  successFooter: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  resultCopyBlock: {
    alignItems: 'center',
    gap: 2,
  },
  resultCta: {
    alignSelf: 'stretch',
    minHeight: 52,
    borderRadius: 12,
    marginTop: spacing.sm,
  },
  actionChoiceRow: {
    flexDirection: 'row',
    alignSelf: 'stretch',
    gap: spacing.md,
  },
  actionChoiceButton: { flex: 1 },
});
